import dayjs, { Dayjs } from "dayjs";

/**
 * formatted string looks like:
 * - 05 Apr 2999
 */
export const dateDisplayFormat = "DD MMM YYYY";

/**
 * formatted string looks like:
 * - 2025-05-15
 */
export const dateDisplayFormatInternational = "YYYY-MM-DD";

/**
 * formatted string looks like:
 * - 05 Apr 2999 14:20
 */
export const dateTimeDisplayFormat = "DD MMM YYYY HH:mm";
/**
 * formatted string looks like:
 * - 12:00
 * - 22:19
 */
export const timeDisplayFormat = "HH:mm";
/**
 * formatted string looks like:
 * - 05 Apr 2999 12:00 (UTC)
 */
export const dateTimeWithTimezoneDisplayFormat = "DD MMM YYYY HH:mm (z)";

/**
 * Some value that represents a valid date.
 *
 * - Strings should be in a format that can be parsed by dayjs.
 * - Numbers will be interpreted as timestamps (milliseconds since epoch).
 */
export type ValidDateRepresentation = string | number | Date | Dayjs;

export const relativeDate = (value: string): string => {
  const date = dayjs(value, "YYYY-MM-DD h:mm:ss");

  // To allow for slight differences in time between server and local
  // for future events to confirm they are at least 1 minute out.
  if (date.isAfter(dayjs()) && date.diff(dayjs(), "minute") > 1) {
    return "Coming up";
  } else if (date.isSame(dayjs(), "day")) {
    return "Today";
  } else if (date.isSame(dayjs().subtract(1, "day"), "day")) {
    return "Yesterday";
  } else if (date.isSame(dayjs(), "week")) {
    return "Earlier this week";
  } else if (date.isSame(dayjs().subtract(1, "week"), "week")) {
    return "Last week";
  } else if (date.isSame(dayjs(), "month")) {
    return "Earlier this month";
  } else if (date.isSame(dayjs().subtract(1, "month"), "month")) {
    return "Last month";
  } else if (date.isSame(dayjs(), "year")) {
    return "Earlier this year";
  } else if (date.isSame(dayjs().subtract(1, "year"), "year")) {
    return "Last year";
  } else {
    return "Older";
  }
};

/**
 * Format a given date as a string in the format {@link dateDisplayFormat}.
 */
export const formatDateString = (value: ValidDateRepresentation | null): string => {
  return dayjs(value).format(dateDisplayFormat); // formatted string looks like 05 Apr 2023
};

/**
 * Format a given date for a given timezone as a string in the format {@link dateDisplayFormat}.
 *
 * Note this does not display the timezone, just the date in the local timezone of the user.
 */
export const formatDateStringWithTimezone = (value: ValidDateRepresentation): string => {
  const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();
  return dayjs.utc(value).tz(timeZone).format(dateDisplayFormat);
};

/**
 * Format a given date for a given timezone as a string in the format {@link dateTimeDisplayFormat}.
 *
 * Note this does not display the timezone, just the date and time in the local timezone of the user.
 */
export const formatDatetimeString = (value: ValidDateRepresentation): string => {
  const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();
  return dayjs.utc(value).tz(timeZone).format(dateTimeDisplayFormat);
};

/**
 * Format a given date as a string in the format {@link dateTimeWithTimezoneDisplayFormat}.
 */
export const formatDateTimeStringWithTimezone = (value: ValidDateRepresentation): string => {
  const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();
  return dayjs.utc(value).tz(timeZone).format(dateTimeWithTimezoneDisplayFormat);
};

/**
 * Return string formatted date as hh:mm A (e.g. 7:42 am) if the date is today's date.
 * Otherwise format date as DD MM YYYY (18 Jan 2023).
 *
 * @param value - timezoned datetime as object or string or number (time since epoch). If no TZ can be inferred, it will default to UTC.
 * @returns The string object representing a time or a date
 */
export const formatToTimeOrDate = (value: ValidDateRepresentation): string => {
  return dayjs.utc(value).local().isSame(dayjs(), "day") ? formatToLocalTime(value) : formatToLocalDate(value);
};

/**
 * Format a given date as a time string in the format {@link timeDisplayFormat}.
 */
export const formatToLocalTime = (value: ValidDateRepresentation): string => {
  return dayjs.utc(value).local().format(timeDisplayFormat);
};

/**
 * Format a given date as a time string in the format {@link dateDisplayFormat}.
 */
export const formatToLocalDate = (value: ValidDateRepresentation): string => {
  return dayjs.utc(value).local().format(dateDisplayFormat);
};

/**
 * Format single digit number by adding a leading 0.
 */
export const formatMonth = (value: number): string => {
  return String(value).padStart(2, "0");
};

export type DurationUnit = "year" | "month" | "week" | "day" | "hour" | "minute" | "second";
export type UnitDisplayStyle = "long" | "short" | "narrow";

/**
 *
 * @param duration - The duration to format
 * @param unit - the unit to use
 * @param display - the display style for the unit, defaults to "long"
 * @returns the formatted duration
 * @example
 *   formatDuration(1234, "minute") // "1,234 minutes"
 *   formatDuration(1234, "minute", "short") // "1,234 min"
 *   formatDuration(1234, "minute", "narrow") // "1,234m"
 *
 */
export function formatDuration(duration: number | undefined | null, unit: DurationUnit, display: UnitDisplayStyle = "long"): string {
  if (duration === null || duration === undefined || Number.isNaN(duration)) return "-";
  return Intl.NumberFormat("en", { style: "unit", unit, unitDisplay: display }).format(duration);
}

/**
 * Format a given number of months as years and months
 * @param months - The number of months to format
 * @returns The formatted string
 * @example
 *  formatMonthsAsYears(12) // "1 year"
 *  formatMonthsAsYears(25) // "2 years 1 month"
 */
export function formatMonthsAsYears(months: number | undefined | null): string {
  if (months == null || Number.isNaN(months) || months === 0) return "-";

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years === 0) {
    return `${formatDuration(remainingMonths, "month")}`;
  }
  if (remainingMonths === 0) {
    return `${formatDuration(years, "year")}`;
  }
  return `${formatDuration(years, "year")} ${formatDuration(remainingMonths, "month")}`;
}
